import { memo, useCallback } from 'react';
import { View } from 'react-native';
import { Props, Listing } from 'shared/@types';
import { StatType } from 'shared/@generated/models';
import { Image } from 'shared/components/image';
import LikeButton from 'shared/components/like-button';
import { Link } from 'shared/components/link';
import { Text } from 'shared/components/text';
import { useRoute } from 'shared/library/routes/route';
import { useFormatter } from 'shared/library/use-formatter';
import { useStatsBatcher } from 'shared/library/stats-batcher';
import { useInViewOnce } from 'shared/library/use-in-view';
import { setListingPreview } from 'shared/state/listing-preview';
import { prefetchListingDetailImage } from 'shared/library/prefetch-listing-detail-image';
import { BreedName } from 'shared/components/breed-name';
import { hasListingVerifiedPedigree } from 'shared/library/listing-pedigree';

const LikedListingCard = memo(function LikedListingCard({ listing }: Props<{ listing: Listing }>) {
  const { formatCurrency } = useFormatter();
  const { batchIncrementStats } = useStatsBatcher();
  const route = useRoute();
  const mediaAsset = listing.media_assets?.[0] ?? listing.avatar;
  const hasVerifiedPedigree = hasListingVerifiedPedigree(listing);

  const logStats = useCallback(
    (statType: StatType) => {
      if (!mediaAsset) return;

      batchIncrementStats(mediaAsset.uuid, statType);
    },
    [mediaAsset?.uuid, batchIncrementStats],
  );

  const ref = useInViewOnce(() => {
    logStats(StatType.Impression);
  });

  return (
    <View ref={ref} className={'relative h-full'} testID={`listing-card-${listing.slug}`}>
      <View className={'absolute left-0 top-0 z-10 p-2'}>
        <LikeButton
          modelId={listing.id}
          modelType={'App\\Models\\Listing'}
          testID={`listing-card-like-${listing.slug}`}
        />
      </View>

      <Link
        containerClassName={
          'group h-full overflow-hidden rounded-[10px] bg-white shadow-[0_2px_10px_rgba(46,27,16,0.08)]'
        }
        href={route('listings.show', { listing: listing.slug })}
        asBlock
        onPress={() => {
          setListingPreview(listing);
          prefetchListingDetailImage(listing);
          logStats(StatType.Click);
        }}
      >
        <Image
          src={mediaAsset}
          alt={listing.title}
          containerClassName={'aspect-4/3 w-full bg-white'}
          imageClassName={'object-cover transition duration-200 group-hover:brightness-95'}
          sizeType={'card'}
        />

        <View className={'border-brand-brown-dark/15 flex-1 border border-t-0 bg-white p-3'}>
          <View className={'flex-row items-center gap-1.5'}>
            <Text
              className={'text-brand-brown-dark min-w-0 shrink font-bold group-hover:underline'}
            >
              <BreedName
                name={listing.breed_name}
                animal={listing.animal}
                breedType={listing.breed_type}
                hasVerifiedPedigree={hasVerifiedPedigree}
              />
            </Text>
          </View>
          <Text className={'text-base font-black text-green-600 group-hover:underline'}>
            {formatCurrency(listing.price!, listing.currency!)}
          </Text>
        </View>
      </Link>
    </View>
  );
});

export { LikedListingCard };
